import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Typography,
  Skeleton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import tributeServices from "../../services/tributeServices";
import * as tributeCommentServices from "../../services/tributeCommentServices";
import { toast } from "react-toastify";
import ImageWithFallback from "../../components/ImageWithFallback";
import "./TributeDetail.css";
import "../../components/RichTextEditor/richtext.css";

const ContentWrap = styled(Box)({
  maxWidth: 1200,
  margin: "0 auto",
  padding: "0 24px",
});

const CommentSectionWrap = styled(Box)(({ theme }) => ({
  marginTop: 48,
  paddingTop: 32,
  borderTop: `2px solid ${theme.palette.divider}`,
}));

const CommentCard = styled(Box)(({ theme }) => ({
  padding: "16px 20px",
  borderRadius: 12,
  backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b",
  marginBottom: 12,
  "&:last-of-type": { marginBottom: 0 },
}));

const CommentFormWrap = styled(Box)({
  marginBottom: 24,
});

const COMMENTS_PER_PAGE = 50;

const TributeDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Comment flow state
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [openNameEmailDialog, setOpenNameEmailDialog] = useState(false);
  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pendingComment, setPendingComment] = useState(null);
  const [otpError, setOtpError] = useState("");
  const [commentPage, setCommentPage] = useState(1);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleCommentSubmit = () => {
    const trimmed = commentText.trim();
    if (!trimmed) return;
    setPendingComment(trimmed);
    setOpenNameEmailDialog(true);
  };

  const handleNameEmailSubmit = () => {
    if (!name.trim() || !email.trim() || !id) return;
    setSendingOtp(true);
    tributeCommentServices
      .sendOtp(id, { name: name.trim(), email: email.trim(), commentText: pendingComment })
      .then(() => {
        setOpenNameEmailDialog(false);
        setOpenOtpDialog(true);
        setOtpError("");
        setOtp("");
        toast.success("Verification code sent to your email.");
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message || "Failed to send code.";
        toast.error(msg);
      })
      .finally(() => setSendingOtp(false));
  };

  const handleOtpVerify = () => {
    if (otp.length !== 6) {
      setOtpError("Please enter a 6-digit OTP.");
      return;
    }
    if (!id) return;
    setVerifying(true);
    tributeCommentServices
      .verifyOtp(id, {
        name: name.trim(),
        email: email.trim(),
        commentText: pendingComment,
        otp: otp.trim(),
      })
      .then((res) => {
        const newComment = res.data;
        setComments((prev) => [
          {
            id: newComment.id,
            name: newComment.name,
            text: newComment.text,
            createdAt: newComment.createdAt,
          },
          ...prev,
        ]);
        setCommentText("");
        setPendingComment(null);
        setName("");
        setEmail("");
        setOtp("");
        setOpenOtpDialog(false);
        setOtpError("");
        setCommentPage(1);
        toast.success("Comment published.");
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message || "Verification failed.";
        setOtpError(msg);
      })
      .finally(() => setVerifying(false));
  };

  const totalCommentPages = Math.max(1, Math.ceil(comments.length / COMMENTS_PER_PAGE));
  const paginatedComments = comments.slice(
    (commentPage - 1) * COMMENTS_PER_PAGE,
    commentPage * COMMENTS_PER_PAGE
  );

  const handleCloseNameEmail = () => {
    setOpenNameEmailDialog(false);
    setPendingComment(null);
  };

  const handleCloseOtp = () => {
    setOpenOtpDialog(false);
    setOtp("");
    setOtpError("");
  };

  useEffect(() => {
    if (!id) return;
    tributeServices
      .getByID(id)
      .then((res) => setData(res?.data?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id || loading) return;
    setCommentsLoading(true);
    tributeCommentServices
      .getComments(id)
      .then((res) => setComments(res.data || []))
      .catch(() => setComments([]))
      .finally(() => setCommentsLoading(false));
  }, [id, loading]);

  if (loading) {
    return (
      <Box sx={{ py: 6 }}>
        <ContentWrap>
          <Skeleton height={36} width={180} />
          <Skeleton variant="rectangular" height={320} sx={{ mt: 3, borderRadius: 2 }} />
          <Skeleton height={40} sx={{ mt: 3 }} />
          <Skeleton height={24} width="90%" />
        </ContentWrap>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <ContentWrap>
          <Typography variant="h6" color="text.secondary">Tribute not found.</Typography>
          <Button component={Link} to="/tributes" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
            Back to Tributes
          </Button>
        </ContentWrap>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2, pb: 6, minHeight: "60vh" }}>
      <ContentWrap>
        <Button
          component={Link}
          to="/tributes"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 0, mb: 1 }}
        >
          Back to Tributes
        </Button>

        {data.shortStatement && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" className="tribute-short-statement" sx={{ fontSize: "1.35rem", fontWeight: 700 }}>
              {data.shortStatement}
            </Typography>
          </Box>
        )}

        {data.image && (
          <Box
            sx={{
              mb: 3,
              width: "100%",
              maxWidth: 800,
              height: 480,
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ImageWithFallback
              path={data.image}
              alt={data.name || "Tribute"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
                borderRadius: "12px",
              }}
            />
          </Box>
        )}

        {data.description && (
          <Box
            className="tribute-detail-description rich-text-content"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        )}

        {/* Comment Section */}
        <CommentSectionWrap>
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, fontWeight: 700 }}>
            <ChatBubbleOutlineIcon fontSize="small" />
            Comments ({comments.length})
          </Typography>
          {comments.length > COMMENTS_PER_PAGE && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Showing {(commentPage - 1) * COMMENTS_PER_PAGE + 1}–{Math.min(commentPage * COMMENTS_PER_PAGE, comments.length)} of {comments.length}
            </Typography>
          )}

          {commentsLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Loading comments…
            </Typography>
          )}
          <CommentFormWrap>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
                mb: 1.5,
              }}
            />
            <Button
              variant="contained"
              onClick={handleCommentSubmit}
              disabled={!commentText.trim()}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                backgroundColor: "#1a80c8",
                "&:hover": { backgroundColor: "#1565a0" },
              }}
            >
              Submit Comment
            </Button>
          </CommentFormWrap>

          <Box>
            {paginatedComments.map((c) => (
              <CommentCard key={c.id}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1a80c8", mb: 0.5 }}>
                  {c.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {c.createdAt
                    ? new Date(c.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                    : ""}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6 }}>
                  {c.text}
                </Typography>
              </CommentCard>
            ))}
          </Box>
          {totalCommentPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={totalCommentPages}
                page={commentPage}
                onChange={(_, p) => setCommentPage(p)}
                color="primary"
                showFirstButton
                showLastButton
                sx={{ "& .MuiPaginationItem-root": { borderRadius: 2 } }}
              />
            </Box>
          )}
        </CommentSectionWrap>
      </ContentWrap>

      {/* Name & Email Dialog */}
      <Dialog open={openNameEmailDialog} onClose={handleCloseNameEmail} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>Verify your identity</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Please enter your name and email to continue. We'll send you a one-time code to verify.
          </Typography>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button onClick={handleCloseNameEmail} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleNameEmailSubmit}
            disabled={!name.trim() || !email.trim() || sendingOtp}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: "#1a80c8",
              "&:hover": { backgroundColor: "#1565a0" },
            }}
          >
            {sendingOtp ? "Sending…" : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog open={openOtpDialog} onClose={handleCloseOtp} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>Enter verification code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            We've sent a 6-digit code to <strong>{email}</strong>. Enter it below.
          </Typography>
          <TextField
            fullWidth
            label="OTP Code"
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
              setOtpError("");
            }}
            placeholder="000000"
            margin="normal"
            error={!!otpError}
            helperText={otpError}
            inputProps={{ maxLength: 6, style: { letterSpacing: 8, fontSize: "1.25rem", textAlign: "center" } }}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button onClick={handleCloseOtp} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleOtpVerify}
            disabled={otp.length !== 6 || verifying}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: "#1a80c8",
              "&:hover": { backgroundColor: "#1565a0" },
            }}
          >
            {verifying ? "Verifying…" : "Verify & Publish Comment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TributeDetail;
